import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const REGIONS = [
  {
    id: "frontal-l",
    name: "Left Frontal Lobe",
    x: 228,
    y: 164,
    z: 72,
    color: "#f59e0b",
    network: "Major Lobe",
    description:
      "Inputs → multimodal association cortex, language-dominant cortical streams, basal ganglia-thalamocortical loops, and goal-state information held across frontal circuitry. Local computation → sustained recurrent firing keeps task rules and ordered action steps active across time, while competitive selection mechanisms amplify one candidate action sequence and suppress incompatible alternatives. Output target → premotor cortex, supplementary motor regions, striatal loops, and other frontal association areas. Network effect → converts abstract goals into structured, serially organized cognitive and motor programs, especially for deliberate action planning and language-linked sequencing.",
  },
  {
    id: "frontal-r",
    name: "Right Frontal Lobe",
    x: 412,
    y: 164,
    z: 72,
    color: "#fb923c",
    network: "Major Lobe",
    description:
      "Inputs → contextual monitoring signals, salience-related inputs, attentional state information, and feedback about whether current behavior remains appropriate. Local computation → inhibitory control circuitry increases or relaxes response gating, monitors mismatch between present demands and ongoing behavior, and raises control gain when interruption, stopping, or reorientation is needed. Output target → motor-control regions, frontoparietal control systems, and subcortical action-selection pathways. Network effect → suppresses prepotent but inappropriate responses and shifts the system toward vigilance, reevaluation, and context-sensitive behavioral adjustment.",
  },
  {
    id: "parietal-l",
    name: "Left Parietal Lobe",
    x: 252,
    y: 110,
    z: 20,
    color: "#22c55e",
    network: "Major Lobe",
    description:
      "Inputs → somatosensory information, proprioceptive position signals, visual-spatial coordinates, and learned symbolic-action associations. Local computation → transforms raw body and space information into structured coordinate maps that can be referenced for reaching, manipulation, calculation, and rule-based operations. Output target → premotor cortex, frontal executive systems, and distributed networks involved in learned action schemas. Network effect → provides an action-usable representation of where body parts and objects are in relation to each other so higher systems can perform organized movement and symbolic operations.",
  },
  {
    id: "parietal-r",
    name: "Right Parietal Lobe",
    x: 388,
    y: 110,
    z: 20,
    color: "#4ade80",
    network: "Major Lobe",
    description:
      "Inputs → multimodal sensory streams carrying visual, vestibular, proprioceptive, and external spatial information. Local computation → integrates those signals into broad egocentric and allocentric maps, then redistributes attentional weighting across the spatial field according to behavioral relevance. Output target → dorsal attention systems, frontal control regions, and orienting circuitry. Network effect → maintains large-scale awareness of the surrounding environment and biases processing toward where attention should be deployed in external space.",
  },
  {
    id: "temporal-l",
    name: "Left Temporal Lobe",
    x: 208,
    y: 236,
    z: 70,
    color: "#3b82f6",
    network: "Major Lobe",
    description:
      "Inputs → temporally structured auditory information, phonological patterns, and links from lexical-semantic memory systems. Local computation → hierarchically parses sound into increasingly abstract units, mapping acoustic sequences onto phonemes, words, and semantic associations stored across temporal and distributed cortical networks. Output target → language comprehension systems, frontal language-control regions, and memory-related semantic networks. Network effect → converts auditory streams into stable meaning-bearing representations that can be used for understanding speech, verbal memory access, and conceptual retrieval.",
  },
  {
    id: "temporal-r",
    name: "Right Temporal Lobe",
    x: 432,
    y: 236,
    z: 70,
    color: "#60a5fa",
    network: "Major Lobe",
    description:
      "Inputs → complex auditory and visual-social signals including prosody, voice identity, facial configuration, and contextual scene information. Local computation → extracts broad pattern structure, affective tone, interpersonal nuance, and nonliteral contextual cues rather than focusing mainly on literal lexical content. Output target → social-cognitive systems, emotional valuation circuitry, and associative interpretation networks. Network effect → enriches perception with tone, social significance, and gestalt-level interpretation so signals are understood as expressive events, not just symbolic content.",
  },
  {
    id: "occipital-l",
    name: "Left Occipital Lobe",
    x: 270,
    y: 184,
    z: -58,
    color: "#a855f7",
    network: "Major Lobe",
    description:
      "Inputs → retinotopically organized visual signals relayed from the thalamus. Local computation → decomposes raw image input into contrast boundaries, line orientation, local motion, spatial frequency, and edge geometry while preserving fine spatial organization. Output target → ventral and dorsal visual streams that perform object recognition and visually guided action. Network effect → supplies the first structured visual primitives needed for higher cortical systems to determine what is being seen and where it is located.",
  },
  {
    id: "occipital-r",
    name: "Right Occipital Lobe",
    x: 370,
    y: 184,
    z: -58,
    color: "#c084fc",
    network: "Major Lobe",
    description:
      "Inputs → retinotopic visual input carrying luminance, contrast, motion, and local spatial structure. Local computation → preserves spatial layout while extracting low-level form and movement features into an organized code that downstream regions can further integrate. Output target → scene-processing, motion-guidance, and recognition pathways across dorsal and ventral networks. Network effect → transforms retinal data into structured visual components that support scene interpretation, orientation, and visually guided behavior.",
  },
  {
    id: "cerebellum",
    name: "Cerebellum",
    x: 320,
    y: 304,
    z: -78,
    color: "#e11d48",
    network: "Major Lobe",
    description:
      "Inputs → copies of intended motor commands, sensory feedback about ongoing body state, and timing information from distributed cortical-subcortical systems. Local computation → compares predicted trajectories with actual feedback, computes deviation and timing error, and updates internal predictive models that reduce noise and overshoot. Output target → motor systems, balance pathways, and related cognitive sequencing circuits through thalamic relays. Network effect → refines movement and timing by continuously correcting mismatch between intended and actual performance, producing smoother and more precisely calibrated output.",
  },
  {
    id: "brainstem",
    name: "Brainstem",
    x: 320,
    y: 342,
    z: 5,
    color: "#eab308",
    network: "Major Lobe",
    description:
      "Inputs → visceral sensory signals, autonomic feedback, cortical and hypothalamic descending regulation, and ascending sensory traffic from the body. Local computation → brainstem nuclei regulate respiratory rhythm, cardiovascular tone, autonomic balance, reflex integration, and ascending neuromodulatory arousal. Output target → spinal cord, autonomic effectors, thalamus, cortex, and widespread state-regulation systems. Network effect → sets the baseline physiological operating condition of the whole nervous system, controlling wakefulness, reactivity, and life-support functions on which higher cognition depends.",
  },

  {
    id: "dlpfc-l",
    name: "Left Dorsolateral Prefrontal Cortex",
    x: 224,
    y: 145,
    z: 88,
    color: "#14b8a6",
    network: "Central Executive Network",
    description:
      "Inputs → task goals, symbolic content, working-memory items, and rule signals from broader frontoparietal circuits. Local computation → recurrent cortical activity keeps intermediate variables, constraints, and ordered steps active even when they are absent from sensory input, while selective amplification manipulates those representations according to current rules. Output target → posterior parietal cortex, premotor systems, language-related control regions, and decision-selection loops. Network effect → enables multi-step reasoning by holding and transforming information online so cognition can operate on internally maintained content rather than only immediate sensory input.",
  },
  {
    id: "dlpfc-r",
    name: "Right Dorsolateral Prefrontal Cortex",
    x: 416,
    y: 145,
    z: 88,
    color: "#2dd4bf",
    network: "Central Executive Network",
    description:
      "Inputs → current task-state representations, attentional competition signals, and evidence that distraction or drift is occurring. Local computation → monitors whether the active task set remains dominant, increases top-down control when competing inputs gain strength, and reinstates the currently relevant rule configuration when processing destabilizes. Output target → parietal priority systems, inhibitory frontal circuits, and control-related subcortical loops. Network effect → stabilizes executive control by ensuring that relevant content remains selected and distractors do not take over the processing hierarchy.",
  },
  {
    id: "ppc-l",
    name: "Left Posterior Parietal Cortex",
    x: 246,
    y: 118,
    z: 36,
    color: "#0f766e",
    network: "Central Executive Network",
    description:
      "Inputs → sensory-spatial information, symbolic task elements, and executive relevance signals from frontal control systems. Local computation → binds currently relevant features into stable priority maps and intermediate representational buffers that can be queried by working-memory and control circuits. Output target → dorsolateral prefrontal cortex, motor planning regions, and broader attentional workspace networks. Network effect → keeps task-relevant content accessible in a structured format so executive systems can select, compare, and manipulate the information needed for controlled cognition.",
  },
  {
    id: "ppc-r",
    name: "Right Posterior Parietal Cortex",
    x: 394,
    y: 118,
    z: 36,
    color: "#0d9488",
    network: "Central Executive Network",
    description:
      "Inputs → competing sensory and internal representations along with relevance-weighting signals from frontal and salience-related systems. Local computation → allocates attentional gain toward the most behaviorally important locations, objects, or mental representations while suppressing less relevant competitors. Output target → frontal executive circuitry, orienting systems, and distributed attentional networks. Network effect → determines which signals remain in the active executive workspace and continue receiving processing resources.",
  },

  {
    id: "mpfc",
    name: "Medial Prefrontal Cortex",
    x: 320,
    y: 145,
    z: 48,
    color: "#f43f5e",
    network: "Default Mode Network",
    description:
      "Inputs → autobiographical memory fragments, internal simulations, affective-value signals, and social self-referential information. Local computation → links internally generated content to the self-model by assigning personal relevance, self-implication, and goal-related value to imagined or remembered events. Output target → other default mode hubs, valuation circuitry, and higher-order reflective systems. Network effect → transforms internally generated material from neutral content into self-relevant meaning, helping define what an internal thought or memory implies for the organism.",
  },
  {
    id: "pcc",
    name: "Posterior Cingulate Cortex and Precuneus",
    x: 320,
    y: 190,
    z: -26,
    color: "#fb7185",
    network: "Default Mode Network",
    description:
      "Inputs → distributed memory traces, scene-construction signals, self-related context, and internally directed attentional information. Local computation → integrates these sources into a coherent background frame or inner vantage point from which autobiographical and imagined content can be organized. Output target → medial prefrontal, parietal associative, and broader default mode regions. Network effect → stabilizes internally oriented cognition by coordinating self-context, perspective, and mental scene structure into a unified internal frame of reference.",
  },
  {
    id: "ang-l",
    name: "Left Angular Gyrus",
    x: 255,
    y: 166,
    z: -8,
    color: "#be123c",
    network: "Default Mode Network",
    description:
      "Inputs → semantic memory, linguistic content, mnemonic fragments, and abstract relational information from distributed association cortex. Local computation → combines these diverse inputs into higher-order conceptual structures, linking separate pieces of information into unified semantic or narrative wholes. Output target → default mode hubs, language-linked association systems, and reflective cognition networks. Network effect → supports meaning construction by assembling fragmented knowledge into interpretable conceptual and narrative structure.",
  },
  {
    id: "ang-r",
    name: "Right Angular Gyrus",
    x: 385,
    y: 166,
    z: -8,
    color: "#e11d48",
    network: "Default Mode Network",
    description:
      "Inputs → contextual cues, social-perspective information, associative memory fragments, and nonliteral meaning signals. Local computation → binds those cues into a wider situational frame, especially when interpretation depends on viewpoint, implication, or implicit context rather than direct literal content. Output target → default mode integrative hubs and higher-order interpretive systems. Network effect → builds broader contextual and perspective-based meaning around internally generated or socially interpreted material.",
  },

  {
    id: "ainsula-l",
    name: "Left Anterior Insula",
    x: 256,
    y: 220,
    z: 60,
    color: "#f97316",
    network: "Salience Network",
    description:
      "Inputs → visceral, autonomic, somatic, and internal state signals reflecting ongoing bodily condition. Local computation → compresses distributed interoceptive input into a higher-order representation of current body feeling and marks that representation as relevant to cognition. Output target → salience network hubs, conscious awareness systems, and control-related cortical regions. Network effect → translates raw internal physiology into a consciously accessible signal about how the body currently feels and why that state matters for behavior.",
  },
  {
    id: "ainsula-r",
    name: "Right Anterior Insula",
    x: 384,
    y: 220,
    z: 60,
    color: "#ea580c",
    network: "Salience Network",
    description:
      "Inputs → interoceptive urgency signals, novelty, uncertainty, unexpected external events, and context-dependent relevance cues. Local computation → rapidly integrates internal and external evidence for significance, then triggers large-scale reallocation of processing resources once a relevance threshold is crossed. Output target → dorsal anterior cingulate cortex, executive control networks, and network-switching pathways. Network effect → helps pull the brain out of background or internally absorbed processing and shift it toward externally mobilized control when something important demands action.",
  },
  {
    id: "dacc",
    name: "Dorsal Anterior Cingulate Cortex",
    x: 320,
    y: 175,
    z: 66,
    color: "#f59e0b",
    network: "Salience Network",
    description:
      "Inputs → competing response tendencies, error-related feedback, effort demand signals, and salience-based indications that control may need to increase. Local computation → detects conflict between active options, estimates whether current control intensity is sufficient, and computes the need for increased monitoring or effort recruitment. Output target → dorsolateral prefrontal cortex, motor-control systems, and wider executive regulation networks. Network effect → escalates cognitive control when processing becomes unstable, conflicted, or effortful, pushing the system into a higher-control operating mode.",
  },
];

const CONNECTIONS = [
  ["frontal-l", "parietal-l"],
  ["frontal-r", "parietal-r"],
  ["frontal-l", "temporal-l"],
  ["frontal-r", "temporal-r"],
  ["parietal-l", "occipital-l"],
  ["parietal-r", "occipital-r"],
  ["temporal-l", "occipital-l"],
  ["temporal-r", "occipital-r"],
  ["frontal-l", "frontal-r"],
  ["parietal-l", "parietal-r"],
  ["temporal-l", "temporal-r"],
  ["brainstem", "cerebellum"],

  ["dlpfc-l", "ppc-l"],
  ["dlpfc-r", "ppc-r"],
  ["dlpfc-l", "dlpfc-r"],
  ["ppc-l", "ppc-r"],

  ["mpfc", "pcc"],
  ["pcc", "ang-l"],
  ["pcc", "ang-r"],
  ["ang-l", "ang-r"],

  ["ainsula-l", "dacc"],
  ["ainsula-r", "dacc"],
  ["ainsula-l", "ainsula-r"],

  ["dacc", "dlpfc-l"],
  ["dacc", "dlpfc-r"],
  ["dacc", "mpfc"],
  ["ainsula-l", "dlpfc-l"],
  ["ainsula-r", "dlpfc-r"],
];

const FILTERS = [
  "All",
  "Major Lobe",
  "Central Executive Network",
  "Default Mode Network",
  "Salience Network",
];

const NETWORK_COLORS = {
  "Major Lobe": "#94a3b8",
  "Central Executive Network": "#14b8a6",
  "Default Mode Network": "#f43f5e",
  "Salience Network": "#f97316",
};

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function hexToRgb(hex) {
  const clean = String(hex).replace("#", "");
  const bigint = parseInt(clean, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function projectPoint(region, yawDeg, pitchDeg) {
  const yaw = (yawDeg * Math.PI) / 180;
  const pitch = (pitchDeg * Math.PI) / 180;

  let x = region.x - 320;
  let y = region.y - 215;
  let z = region.z;

  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);
  const x1 = x * cosY + z * sinY;
  const z1 = -x * sinY + z * cosY;

  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  const y2 = y * cosP - z1 * sinP;
  const z2 = y * sinP + z1 * cosP;

  const perspective = 1 + z2 / 320;

  return {
    ...region,
    px: 320 + x1 * perspective,
    py: 215 + y2 * perspective,
    pz: z2,
    scale: Math.max(0.66, 1 + z2 / 360),
  };
}

function BrainAnatomy({ yaw, pitch }) {
  const yawShift = yaw * 0.7;
  const pitchShift = pitch * 0.6;
  const widthScale = 1 - Math.min(Math.abs(yaw) / 260, 0.12);
  const heightScale = 1 + pitch / 240;

  return (
    <g>
      <defs>
        <radialGradient id="brainBaseGrad" cx="34%" cy="24%">
          <stop offset="0%" stopColor="#ffe5e5" />
          <stop offset="18%" stopColor="#f6caca" />
          <stop offset="45%" stopColor="#dfa6a6" />
          <stop offset="72%" stopColor="#c27f7f" />
          <stop offset="100%" stopColor="#8d5757" />
        </radialGradient>

        <radialGradient id="brainGlossGrad" cx="28%" cy="20%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="22%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="brainShadowGrad" cx="70%" cy="76%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.24" />
        </radialGradient>

        <radialGradient id="cerebellumGrad" cx="40%" cy="28%">
          <stop offset="0%" stopColor="#ef9db3" />
          <stop offset="55%" stopColor="#cf6d8d" />
          <stop offset="100%" stopColor="#8e3a58" />
        </radialGradient>

        <linearGradient id="brainstemGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f1d16a" />
          <stop offset="50%" stopColor="#c69b1e" />
          <stop offset="100%" stopColor="#8d6700" />
        </linearGradient>

        <filter id="brainDropShadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow
            dx="0"
            dy="18"
            stdDeviation="18"
            floodColor="#000000"
            floodOpacity="0.22"
          />
        </filter>

        <filter id="sulcusShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy="1"
            stdDeviation="1.5"
            floodColor="#4d2323"
            floodOpacity="0.18"
          />
        </filter>

        <filter id="nodeGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <radialGradient id="stageGlow" cx="50%" cy="44%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.14)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.04)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>

      <ellipse cx="320" cy="220" rx="260" ry="170" fill="url(#stageGlow)" />

      <ellipse
        cx="320"
        cy="246"
        rx={220 * widthScale}
        ry={146 * heightScale}
        fill="rgba(0,0,0,0.18)"
        filter="url(#brainDropShadow)"
      />

      <path
        d={`
          M ${116 + yawShift * 0.18} ${218 + pitchShift}
          C ${122 + yawShift * 0.12} ${136 + pitchShift}, 182 ${76 + pitchShift}, 258 ${80 + pitchShift}
          C 278 ${60 + pitchShift}, 304 ${52 + pitchShift}, 320 ${58 + pitchShift}
          C 336 ${52 + pitchShift}, 362 ${60 + pitchShift}, 382 ${80 + pitchShift}
          C 458 ${76 + pitchShift}, ${518 - yawShift * 0.12} ${138 + pitchShift}, ${524 - yawShift * 0.18} ${220 + pitchShift}
          C ${520 - yawShift * 0.12} ${286 + pitchShift}, 480 ${326 + pitchShift}, 430 ${340 + pitchShift}
          C 410 ${360 + pitchShift}, 370 ${372 + pitchShift}, 320 ${366 + pitchShift}
          C 270 ${372 + pitchShift}, 230 ${360 + pitchShift}, 210 ${340 + pitchShift}
          C 160 ${326 + pitchShift}, ${120 + yawShift * 0.12} ${286 + pitchShift}, ${116 + yawShift * 0.18} ${218 + pitchShift}
          Z
        `}
        fill="url(#brainBaseGrad)"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth="2"
      />

      <path
        d={`
          M 320 78
          C 311 108, 311 142, 316 176
          C 320 208, 324 236, 321 266
          C 318 294, 314 320, 320 346
        `}
        fill="none"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="5 7"
      />

      <ellipse
        cx={242 + yawShift * 0.5}
        cy={150 + pitchShift * 0.4}
        rx="120"
        ry="74"
        fill="url(#brainGlossGrad)"
        opacity="0.65"
      />

      <ellipse
        cx={372 - yawShift * 0.25}
        cy={252 + pitchShift * 0.3}
        rx="170"
        ry="104"
        fill="url(#brainShadowGrad)"
        opacity="0.42"
      />

      <g
        fill="none"
        stroke="rgba(121,53,53,0.34)"
        strokeWidth="4"
        strokeLinecap="round"
        filter="url(#sulcusShadow)"
      >
        <path d="M 144 126 C 172 98, 204 98, 238 124 C 258 140, 274 142, 286 126" />
        <path d="M 134 154 C 176 118, 220 118, 260 150 C 276 164, 290 166, 304 154" />
        <path d="M 130 184 C 176 152, 220 150, 268 182 C 286 194, 302 196, 314 188" />
        <path d="M 132 214 C 180 186, 224 184, 272 214 C 290 226, 304 228, 316 222" />
        <path d="M 140 244 C 184 218, 226 220, 270 246 C 284 256, 298 260, 308 254" />
        <path d="M 152 274 C 190 252, 226 252, 262 278 C 278 290, 292 292, 302 286" />
        <path d="M 170 302 C 204 284, 230 284, 254 304" />

        <path d="M 188 104 C 206 126, 210 144, 198 166" strokeWidth="3" />
        <path d="M 232 98 C 248 122, 250 144, 240 170" strokeWidth="3" />
        <path d="M 262 116 C 272 138, 272 158, 264 178" strokeWidth="3" />
        <path d="M 210 208 C 220 228, 220 248, 208 268" strokeWidth="3" />
        <path d="M 254 202 C 266 224, 266 248, 252 272" strokeWidth="3" />
      </g>

      <g
        fill="none"
        stroke="rgba(121,53,53,0.34)"
        strokeWidth="4"
        strokeLinecap="round"
        filter="url(#sulcusShadow)"
      >
        <path d="M 496 126 C 468 98, 436 98, 402 124 C 382 140, 366 142, 354 126" />
        <path d="M 506 154 C 464 118, 420 118, 380 150 C 364 164, 350 166, 336 154" />
        <path d="M 510 184 C 464 152, 420 150, 372 182 C 354 194, 338 196, 326 188" />
        <path d="M 508 214 C 460 186, 416 184, 368 214 C 350 226, 336 228, 324 222" />
        <path d="M 500 244 C 456 218, 414 220, 370 246 C 356 256, 342 260, 332 254" />
        <path d="M 488 274 C 450 252, 414 252, 378 278 C 362 290, 348 292, 338 286" />
        <path d="M 470 302 C 436 284, 410 284, 386 304" />

        <path d="M 452 104 C 434 126, 430 144, 442 166" strokeWidth="3" />
        <path d="M 408 98 C 392 122, 390 144, 400 170" strokeWidth="3" />
        <path d="M 378 116 C 368 138, 368 158, 376 178" strokeWidth="3" />
        <path d="M 430 208 C 420 228, 420 248, 432 268" strokeWidth="3" />
        <path d="M 386 202 C 374 224, 374 248, 388 272" strokeWidth="3" />
      </g>

      <g
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M 150 142 C 192 112, 236 114, 274 146" />
        <path d="M 148 224 C 194 196, 236 198, 280 226" />
        <path d="M 490 142 C 448 112, 404 114, 366 146" />
        <path d="M 492 224 C 446 196, 404 198, 360 226" />
      </g>

      <ellipse
        cx={238 + yawShift * 0.4}
        cy={142 + pitchShift * 0.15}
        rx="62"
        ry="40"
        fill="rgba(255,255,255,0.08)"
      />
      <ellipse
        cx={402 - yawShift * 0.2}
        cy={148 + pitchShift * 0.15}
        rx="58"
        ry="38"
        fill="rgba(255,255,255,0.05)"
      />

      <ellipse
        cx="320"
        cy="310"
        rx="88"
        ry="56"
        fill="url(#cerebellumGrad)"
        opacity="0.98"
      />
      <g
        fill="none"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth="3"
        strokeLinecap="round"
      >
        <path d="M 248 304 C 266 292, 286 292, 304 304" />
        <path d="M 270 322 C 290 310, 314 310, 334 322" />
        <path d="M 304 338 C 316 332, 324 332, 336 338" />
        <path d="M 336 304 C 354 292, 374 292, 392 304" />
        <path d="M 350 322 C 364 314, 380 314, 392 322" />
      </g>

      <rect
        x="302"
        y="316"
        width="36"
        height="100"
        rx="18"
        fill="url(#brainstemGrad)"
      />
      <ellipse cx="320" cy="410" rx="18" ry="11" fill="rgba(255,255,255,0.08)" />

      <ellipse
        cx="320"
        cy="210"
        rx="184"
        ry="120"
        fill="rgba(255,255,255,0.025)"
      />
    </g>
  );
}

function RegionNode({ region, active, onClick }) {
  const radius = (active ? 12 : 8.5) * region.scale;
  const labelWidth = 250;
  const badgeColor = NETWORK_COLORS[region.network] || region.color;

  return (
    <g onClick={onClick} style={{ cursor: "pointer" }}>
      <circle
        cx={region.px}
        cy={region.py}
        r={radius + 15}
        fill={rgba(region.color, active ? 0.24 : 0.08)}
        filter="url(#nodeGlow)"
      />
      <circle
        cx={region.px}
        cy={region.py}
        r={radius}
        fill={region.color}
        stroke="white"
        strokeWidth="2"
      />
      <circle
        cx={region.px - radius * 0.3}
        cy={region.py - radius * 0.35}
        r={radius * 0.33}
        fill="rgba(255,255,255,0.33)"
      />
      {active ? (
        <>
          <rect
            x={region.px - labelWidth / 2}
            y={region.py - radius - 42}
            width={labelWidth}
            height="30"
            rx="10"
            fill="rgba(15,23,42,0.94)"
            stroke="rgba(255,255,255,0.12)"
          />
          <rect
            x={region.px - labelWidth / 2 + 8}
            y={region.py - radius - 34}
            width="10"
            height="10"
            rx="999"
            fill={badgeColor}
          />
          <text
            x={region.px + 10}
            y={region.py - radius - 19}
            textAnchor="middle"
            fontSize="10.5"
            fontWeight="700"
            fill="white"
          >
            {region.name}
          </text>
        </>
      ) : null}
    </g>
  );
}

function formatMechanismDescription(description) {
  const parts = String(description).split(/Inputs → |Local computation → |Output target → |Network effect → /).filter(Boolean);
  if (parts.length !== 4) return null;
  return {
    inputs: parts[0].trim(),
    computation: parts[1].trim(),
    outputs: parts[2].trim(),
    effect: parts[3].trim(),
  };
}

function MechanismBlock({ region }) {
  const parsed = formatMechanismDescription(region.description);
  const chipColor = NETWORK_COLORS[region.network] || region.color;

  if (!parsed) {
    return (
      <div
        style={{
          color: "#cbd5e1",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {region.description}
      </div>
    );
  }

  const rowStyle = {
    background: "rgba(255,255,255,0.035)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: "10px 12px",
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 6,
    color: "#e2e8f0",
  };

  const textStyle = {
    color: "#cbd5e1",
    fontSize: 13,
    lineHeight: 1.58,
  };

  return (
    <div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 999,
          background: rgba(chipColor, 0.15),
          border: `1px solid ${rgba(chipColor, 0.3)}`,
          color: "white",
          fontSize: 11,
          fontWeight: 700,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: chipColor,
            display: "inline-block",
          }}
        />
        {region.network}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div style={rowStyle}>
          <div style={labelStyle}>Inputs</div>
          <div style={textStyle}>{parsed.inputs}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Local computation</div>
          <div style={textStyle}>{parsed.computation}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Output target</div>
          <div style={textStyle}>{parsed.outputs}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Network effect</div>
          <div style={textStyle}>{parsed.effect}</div>
        </div>
      </div>
    </div>
  );
}

export default function BrainMap2() {
  const [activeId, setActiveId] = useState("dlpfc-l");
  const [yaw, setYaw] = useState(-10);
  const [pitch, setPitch] = useState(8);
  const [filter, setFilter] = useState("All");
  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    startYaw: 0,
    startPitch: 0,
  });

  const visibleRegions = useMemo(() => {
    if (filter === "All") return REGIONS;
    return REGIONS.filter((r) => r.network === filter);
  }, [filter]);

  const visibleIds = useMemo(
    () => new Set(visibleRegions.map((r) => r.id)),
    [visibleRegions]
  );

  const projected = useMemo(() => {
    return REGIONS.map((region) => projectPoint(region, yaw, pitch)).sort(
      (a, b) => a.pz - b.pz
    );
  }, [yaw, pitch]);

  const regionMap = useMemo(
    () => Object.fromEntries(projected.map((r) => [r.id, r])),
    [projected]
  );

  const activeRegion =
    REGIONS.find((region) => region.id === activeId) || REGIONS[0];

  const handlePointerDown = (e) => {
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startYaw: yaw,
      startPitch: pitch,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!dragRef.current.dragging) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      setYaw(clamp(dragRef.current.startYaw + dx * 0.18, -55, 55));
      setPitch(clamp(dragRef.current.startPitch - dy * 0.14, -24, 24));
    };

    const handlePointerUp = () => {
      dragRef.current.dragging = false;
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 24,
        boxSizing: "border-box",
        background:
          "radial-gradient(circle at top, #1e293b 0%, #0f172a 38%, #020617 100%)",
        color: "white",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1480, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              Interactive Atlas
            </div>
            <h2 style={{ margin: 0, fontSize: 32, lineHeight: 1.05 }}>
              Functional Brain Network Map
            </h2>
          </div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {["Stocks", "ETFs", "Holdings", "Feed"].map((item) => (
              <Link
                key={item}
                to={`/${item.toLowerCase()}`}
                style={{
                  color: "white",
                  textDecoration: "none",
                  padding: "9px 12px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  fontSize: 13,
                }}
              >
                {item}
              </Link>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "440px 1fr",
            gap: 22,
            alignItems: "start",
          }}
        >
          <div
            style={{
              background: "rgba(15,23,42,0.72)",
              backdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 24,
              padding: 18,
              boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
            }}
          >
            <div
              style={{
                borderRadius: 18,
                padding: 16,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
                border: "1px solid rgba(255,255,255,0.08)",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
                Regions
              </div>
              <div
                style={{
                  color: "#cbd5e1",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                Drag the brain with your mouse to rotate it. Click any node to inspect
                that region’s mechanism, connectivity role, and large-scale network
                function.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {Object.entries(NETWORK_COLORS).map(([name, color]) => (
                <div
                  key={name}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 10px",
                    borderRadius: 999,
                    background: rgba(color, 0.12),
                    border: `1px solid ${rgba(color, 0.28)}`,
                    fontSize: 11,
                    color: "#e2e8f0",
                    fontWeight: 700,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: color,
                      display: "inline-block",
                    }}
                  />
                  {name}
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6, color: "#e2e8f0" }}>
                Show
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  color: "white",
                  outline: "none",
                }}
              >
                {FILTERS.map((f) => (
                  <option key={f} value={f} style={{ color: "black" }}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                padding: 14,
                borderRadius: 16,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6, color: "#e2e8f0" }}>
                Horizontal Rotation
              </div>
              <input
                type="range"
                min="-55"
                max="55"
                value={yaw}
                onChange={(e) => setYaw(Number(e.target.value))}
                style={{ width: "100%" }}
              />

              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  marginTop: 14,
                  marginBottom: 6,
                  color: "#e2e8f0",
                }}
              >
                Vertical Rotation
              </div>
              <input
                type="range"
                min="-24"
                max="24"
                value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                style={{ width: "100%" }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 12,
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 12, color: "#94a3b8" }}>
                  {isDragging ? "Dragging..." : "Click and drag the model"}
                </div>
                <button
                  onClick={() => {
                    setYaw(-10);
                    setPitch(8);
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Reset View
                </button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: 8,
                marginBottom: 16,
                maxHeight: 320,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {visibleRegions.map((region) => {
                const selected = region.id === activeId;
                return (
                  <button
                    key={region.id}
                    onClick={() => setActiveId(region.id)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      width: "100%",
                      padding: "12px 12px",
                      borderRadius: 14,
                      border: selected
                        ? `1px solid ${rgba(region.color, 0.55)}`
                        : "1px solid rgba(255,255,255,0.08)",
                      background: selected
                        ? `linear-gradient(180deg, ${rgba(region.color, 0.18)}, rgba(255,255,255,0.05))`
                        : "rgba(255,255,255,0.03)",
                      color: "white",
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: selected ? `0 10px 30px ${rgba(region.color, 0.14)}` : "none",
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 999,
                        background: region.color,
                        flexShrink: 0,
                        marginTop: 4,
                        boxShadow: `0 0 14px ${rgba(region.color, 0.55)}`,
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{region.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        {region.network}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div
              style={{
                minHeight: 320,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.025))",
                borderRadius: 18,
                padding: 14,
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 6, fontSize: 20 }}>
                {activeRegion?.name}
              </div>
              <MechanismBlock region={activeRegion} />
            </div>
          </div>

          <div
            style={{
              background: "rgba(11,18,32,0.72)",
              backdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 28,
              overflow: "hidden",
              minHeight: 860,
              boxShadow: "0 24px 70px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            <div
              style={{
                padding: "14px 18px 0 18px",
                color: "#94a3b8",
                fontSize: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Drag inside the model to rotate</span>
              <span>
                yaw {yaw}° · pitch {pitch}°
              </span>
            </div>

            <svg
              viewBox="0 0 640 440"
              onPointerDown={handlePointerDown}
              style={{
                width: "100%",
                height: "calc(100% - 34px)",
                display: "block",
                cursor: isDragging ? "grabbing" : "grab",
                touchAction: "none",
                userSelect: "none",
                background:
                  "radial-gradient(circle at 50% 26%, rgba(255,255,255,0.12), rgba(255,255,255,0.02) 34%, rgba(255,255,255,0.0) 68%)",
              }}
            >
              <BrainAnatomy yaw={yaw} pitch={pitch} />

              {CONNECTIONS.map(([a, b]) => {
                if (!visibleIds.has(a) || !visibleIds.has(b)) return null;

                const ra = regionMap[a];
                const rb = regionMap[b];
                if (!ra || !rb) return null;

                const highlighted = activeId === a || activeId === b;

                return (
                  <line
                    key={`${a}-${b}`}
                    x1={ra.px}
                    y1={ra.py}
                    x2={rb.px}
                    y2={rb.py}
                    stroke={
                      highlighted
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(148,163,184,0.28)"
                    }
                    strokeWidth={highlighted ? 2.4 : 1.3}
                    strokeLinecap="round"
                  />
                );
              })}

              {projected
                .filter((region) => visibleIds.has(region.id))
                .map((region) => (
                  <RegionNode
                    key={region.id}
                    region={region}
                    active={region.id === activeId}
                    onClick={() => setActiveId(region.id)}
                  />
                ))}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}